import React, { FC, useRef } from 'react';
import { Template } from './components/Template/Template';
import s from './TemplatesGrid.scss';
import { templatesGridDataHooks } from './TemplatesGrid.dataHooks';
import { useTemplates } from '../../hooks/useTemplates';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useExperiments } from '../../contexts/ExperimentsContext';
import { isBlankTemplate } from '../../domain/entities/template';
import { TemplatesCarousel } from '../TemplatesCarousel/TemplatesCarousel';
import { TEMPLATE_LOADING_SCHEMA } from '../../consts';
import { useParamsWithDefaults } from '../../hooks/useParamsWithDefaults';
import cs from 'classnames';
import { ErrorModal } from '../ErrorModal/ErrorModal';
import { TemplatesPageHeading } from '../TemplatesPage/TemplatesPageHeading/TemplatesPageHeading';
import { BlankCanvasButton } from '../BlankCanvasButton/BlankCanvasButton';
import { useTranslation } from '@wix/wix-i18n-config';
import { useSearch } from '../../hooks/useSearch';

export interface TemplatesGridProps {
  heading: string;
  showBlankCanvas: boolean;
}

export const TemplatesGrid: FC<TemplatesGridProps> = ({
  heading,
  showBlankCanvas,
}) => {
  const { templates, loadMore, error, loading } = useTemplates();
  const { criteria } = useSearch();
  const numOfTemplatesInViewport = 6;
  const experiments = useExperiments();
  const { categorySlug } = useParamsWithDefaults();
  const { t } = useTranslation();

  const isEmptyResult = !error && !loading && templates.length === 0;

  const isStudioTemplatesTryBlankCanvasButton = experiments.is(
    'specs.funnel.StudioTemplatesTryBlankCanvasButton',
    'true',
  );

  const isStudioTemplatesHideBlankCanvasTemplate = experiments.is(
    'specs.funnel.StudioTemplatesHideBlankCanvasTemplateOnMobile',
    'true',
  );

  const showCarousel =
    experiments.is(
      'specs.funnel.StudioGalleryInspirationalTemplates',
      'true',
    ) && categorySlug === 'all-templates';

  const isLazyLoadingForTemplatesUsed = experiments.is(
    'specs.funnel.StudioTemplatesTemplateLazyLoading',
    'true',
  );

  const loadingRef = useRef(null);

  useIntersectionObserver(loadingRef, () =>
    loadMore(TEMPLATE_LOADING_SCHEMA.ADDITION),
  );

  return (
    <div>
      <div className={s.headingWrapper}>
        <TemplatesPageHeading
          heading={
            isEmptyResult
              ? t('app.new.title.search.notFound.title', { criteria })
              : heading
          }
          subHeading={
            isEmptyResult
              ? t('app.new.title.search.notFound.subtitle')
              : undefined
          }
        ></TemplatesPageHeading>
        {showBlankCanvas && <BlankCanvasButton className={cs(s.blankCanvas)} />}
      </div>
      <div
        data-hook={templatesGridDataHooks.templatesGrid()}
        className={s.templates}
      >
        {templates?.map((template, index) => (
          <Template
            key={template.templateId}
            template={template}
            isLazyLoad={index > numOfTemplatesInViewport}
            index={index}
            className={cs({
              [s.mobileOnly]:
                isBlankTemplate(template) &&
                isStudioTemplatesTryBlankCanvasButton,
              [s.desktopOnly]:
                isBlankTemplate(template) &&
                isStudioTemplatesHideBlankCanvasTemplate,
            })}
          />
        ))}
        {showCarousel && categorySlug === 'all-templates' && (
          <div className={s.inspirationalCarousel}>
            <TemplatesCarousel />
          </div>
        )}
      </div>
      {isLazyLoadingForTemplatesUsed && <div ref={loadingRef} />}
      {error && <ErrorModal />}
    </div>
  );
};
